import { useEffect, useState } from "react";
import { observer } from "@legendapp/state/react";
import { Play, Pause } from "lucide-react";
import { isPaused$ } from "../../state/videoState";

/**
 * Centre play/pause indicator — replaces the right-rail play button (iOS keeps
 * play/pause in the centre of the video, CloudflarePlayer.swift). A 56px circle,
 * black @ 42% opacity, white glyph. Standard convention: while PAUSED a play (▶)
 * glyph stays visible (the action a tap performs); when PLAYING a pause (❚❚)
 * glyph flashes briefly and then fades out so it doesn't sit on top of the
 * video. Tap the video itself to toggle (VideoPlayer's overlay) — purely visual.
 */
const PLAYING_LINGER_MS = 900;

export const CenterPlayPause = observer(function CenterPlayPause() {
  const paused = isPaused$.get();
  // The only thing that needs an effect is the play-glyph FADE timer (a genuine
  // time-based side effect). Visibility is DERIVED from `paused` + that timer
  // flag — not synced into state — so there's no setState-in-effect for the
  // paused case. While paused the glyph always shows; while playing it shows
  // until the linger elapses.
  const [playFaded, setPlayFaded] = useState(false);

  useEffect(() => {
    if (paused) return; // pause glyph stays; nothing to time
    setPlayFaded(false);
    const t = setTimeout(() => setPlayFaded(true), PLAYING_LINGER_MS);
    return () => clearTimeout(t);
  }, [paused]);

  const visible = paused || !playFaded;

  return (
    <div className="absolute inset-0 z-[15] flex items-center justify-center pointer-events-none">
      <div
        className={`flex items-center justify-center rounded-full transition-opacity duration-300 ${
          visible ? "opacity-100" : "opacity-0"
        }`}
        style={{ width: 56, height: 56, backgroundColor: "rgba(0,0,0,0.42)" }}
      >
        {paused ? (
          <Play className="w-7 h-7 text-white translate-x-[1px]" fill="white" />
        ) : (
          <Pause className="w-7 h-7 text-white" fill="white" />
        )}
      </div>
    </div>
  );
});
